import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, LogIn, Send } from "lucide-react";
import { toast } from "sonner";
import { api, saveAuth } from "../lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    setResent(false);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "email_not_verified") {
        setNeedsVerification(true);
        toast.error(detail.message || "Please confirm your email before logging in.");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally { setLoading(false); }
  };

  const onResend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setResent(true);
    } catch {
      toast.error("Could not send a new link right now. Please try again.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FBBF24] items-center justify-center shadow-[0_8px_0_#B45309] mb-4">
            <Sparkles className="w-10 h-10 text-[#1E1B4B]" strokeWidth={3} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B]">BuzzSpell</h1>
          <p className="text-lg text-slate-600 mt-1 font-semibold">Let's spell some words!</p>
        </div>

        <form onSubmit={onSubmit} className="card-soft space-y-4" data-testid="login-form">
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="you@school.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="••••••"
              data-testid="login-password-input"
            />
            <div className="text-right mt-1.5 mr-2">
              <Link to="/forgot-password" className="text-sm font-bold text-[#4D96FF] hover:underline" data-testid="go-forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-clay w-full" data-testid="login-submit-btn">
            <LogIn className="w-5 h-5" strokeWidth={3} /> {loading ? "Signing in…" : "Let's go!"}
          </button>

          {needsVerification && (
            <div className="rounded-2xl bg-amber-50 border-4 border-amber-200 p-4 text-center space-y-2" data-testid="login-unverified-block">
              <p className="text-sm font-semibold text-amber-800">
                Please confirm your email before logging in.
              </p>
              {resent ? (
                <p className="text-xs text-amber-700">Check your inbox for a fresh confirmation link.</p>
              ) : (
                <button
                  type="button" onClick={onResend} disabled={resending}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-[#4D96FF] hover:underline"
                  data-testid="login-resend-verification-btn"
                >
                  <Send className="w-4 h-4" strokeWidth={3} /> {resending ? "Sending…" : "Resend confirmation email"}
                </button>
              )}
            </div>
          )}

          <p className="text-center text-sm text-slate-600 pt-2">
            New here?{" "}
            <Link to="/register" className="font-bold text-[#4D96FF] hover:underline" data-testid="go-register-link">
              Create an account
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
